import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import Spinner from "features/spinner/Spinner";
import { getStatisticsSummary, selectStatisticsSummary } from "pages/dashboard/statisticsSlice";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import Section from "components/Section";
import OutlinedCard from "components/OutlinedCard";
import SectionHeader from "components/SectionHeader";
import { TimePeriod, TimePeriodValues } from "models/UserStatistics";

const StatisticsSummary = () => {
  const statistcsSummary = useAppSelector(selectStatisticsSummary);
  const dispatch = useAppDispatch();

  const [timePeriod, setTimePeriod] = useState<TimePeriod>("FROM_START");
  const [onlyApproved, setOnlyApproved] = useState<boolean>(true);

  useEffect(() => {
    dispatch(
      getStatisticsSummary({
        timePeriod: timePeriod,
        onlyIncludeApprovedChecklists: onlyApproved,
      })
    );
  }, [timePeriod, onlyApproved]);

  return (
    <Section display="flex" flexDirection="column" flexGrow={1}>
      <SectionHeader title="Checklistor" />

      <OutlinedCard flexGrow={1} display="flex" flexDirection="column">
        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
          <FormControl variant="filled" size="small" sx={{ minWidth: 200, flexGrow: 0 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={(ev) => setTimePeriod(ev.target.value as TimePeriod)}
            >
              <MenuItem value="FROM_START">{TimePeriodValues.FROM_START}</MenuItem>
              <MenuItem value="PAST_WEEK">{TimePeriodValues.PAST_WEEK}</MenuItem>
              <MenuItem value="PAST_MONTH">{TimePeriodValues.PAST_MONTH}</MenuItem>
              <MenuItem value="PAST_YEAR">{TimePeriodValues.PAST_YEAR}</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            label="Endast godkända"
            control={
              <Switch checked={onlyApproved} onChange={() => setOnlyApproved(!onlyApproved)} />
            }
          />
        </Box>

        {statistcsSummary.isLoading ? (
          <Box display="grid" flexGrow={1} sx={{ placeItems: "center" }}>
            <Spinner size="3rem" color="#5f5f5f80" className="mb-3" />
          </Box>
        ) : (
          <>
            <Box sx={{ maxWidth: "min-content", marginInline: "auto" }} textAlign="center" my={2}>
              <Typography fontSize={"clamp(1.5rem, 3vw, 4rem)"} fontWeight="bold" lineHeight={1}>
                {statistcsSummary.totalNumberOfChecklists}
              </Typography>
              <Typography>Skapade checklistor</Typography>
            </Box>

            <Box
              textAlign="center"
              my={2}
              display="flex"
              justifyContent="space-evenly"
              flexWrap="wrap"
            >
              <Box sx={{ maxWidth: "min-content" }}>
                <Typography fontSize={"clamp(2rem, 4vw, 4rem)"} fontWeight="bold" lineHeight={1}>
                  {statistcsSummary.percentageCompleted?.toFixed(1)}
                  <Typography component="span" fontSize={"0.5em"}>
                    %
                  </Typography>
                </Typography>
                <Typography>Slutförda</Typography>
              </Box>

              <Box sx={{ maxWidth: "min-content" }}>
                <Typography fontSize={"clamp(2rem, 4vw, 4rem)"} fontWeight="bold" lineHeight={1}>
                  {statistcsSummary.percentageCompletedInternal?.toFixed(1)}
                  <Typography component="span" fontSize={"0.5em"}>
                    %
                  </Typography>
                </Typography>
                <Typography>Slutförda internt</Typography>
              </Box>
            </Box>
          </>
        )}
      </OutlinedCard>
    </Section>
  );
};

export default StatisticsSummary;

export interface StatisticsSummaryState {
  isLoading: boolean;
  loaded: Boolean;
  totalNumberOfChecklists: number | null;
  numberOfCompletedChecklists: number | null;
  percentageCompleted: number | null;
  percentageCompletedInternal: number | null;
}
